<template>
  <AppLayout title="Painel">
    <div v-if="!resumo" class="text-muted">Carregando...</div>

    <div v-else>
      <div class="row">
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-primary-subtle text-primary"><i class="bi bi-cash-coin"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas hoje</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.sales_today.total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">{{ resumo.sales_today.count }} venda(s)</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-success-subtle text-success"><i class="bi bi-graph-up-arrow"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Vendas no mês</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.sales_month_total) ?? 'R$ 0,00' }}</h3>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon" :class="lucroPositivo ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                <i class="bi" :class="lucroPositivo ? 'bi-piggy-bank' : 'bi-exclamation-triangle'"></i>
              </div>
              <h6 class="card-subtitle text-muted mb-2">Lucro no mês</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.profit_month_total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">Vendas menos descartes</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-warning-subtle text-warning"><i class="bi bi-wallet2"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Contas em aberto</h6>
              <h3 class="mb-0">{{ formaterService.currency(resumo.open_accounts.total) ?? 'R$ 0,00' }}</h3>
              <small class="text-muted">{{ resumo.open_accounts.count }} cliente(s)</small>
            </div>
          </div>
        </div>
        <div class="col-md-3 mb-3">
          <div class="card stat-card h-100">
            <div class="card-body">
              <div class="stat-card__icon bg-info-subtle text-info"><i class="bi bi-safe2"></i></div>
              <h6 class="card-subtitle text-muted mb-2">Caixas abertos agora</h6>
              <h3 class="mb-0">{{ resumo.open_cashiers.length }}</h3>
              <small class="text-muted" v-if="resumo.open_cashiers.length">
                {{ resumo.open_cashiers.map(c => c.operator?.name).join(', ') }}
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-3">
          <h2 class="section-title">Vendas do mês (dia 1 até hoje)</h2>
          <div class="card">
            <div class="card-body">
              <canvas ref="salesChartCanvas" height="90"></canvas>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6 mb-3">
          <h2 class="section-title">Produtos com estoque baixo</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Produto</th>
                  <th>Estoque</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="produto in resumo.low_stock_products" :key="produto.id">
                  <td>{{ produto.name }}</td>
                  <td>{{ produto.stock }} {{ produto.measure }}</td>
                </tr>
                <tr v-if="!resumo.low_stock_products.length">
                  <td colspan="2" class="text-center text-muted">Nenhum produto com estoque baixo.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <router-link :to="{ name: 'produtos' }" class="btn btn-outline-primary btn-sm mt-3">Ver todos os produtos</router-link>
        </div>

        <div class="col-md-6 mb-3">
          <h2 class="section-title">Últimas vendas</h2>
          <div class="table-card">
            <table class="table">
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Data</th>
                  <th>Valor</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="venda in resumo.recent_sales" :key="venda.id">
                  <td>{{ venda.customer?.name ?? 'Avulso' }}</td>
                  <td>{{ formaterService.dateTime(venda.date) }}</td>
                  <td>{{ formaterService.currency(venda.value) }}</td>
                  <td style="width:40px">
                    <router-link :to="{ name: 'sale-detail', params: { id: venda.id } }" class="btn btn-info btn-sm">
                      <i class="bi bi-info-square-fill"></i>
                    </router-link>
                  </td>
                </tr>
                <tr v-if="!resumo.recent_sales.length">
                  <td colspan="4" class="text-center text-muted">Nenhuma venda registrada ainda.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <router-link :to="{ name: 'sales' }" class="btn btn-outline-primary btn-sm mt-3">Ver todas as vendas</router-link>
        </div>
      </div>
    </div>
  </AppLayout>
</template>
<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import Chart from 'chart.js/auto';
import AppLayout from "@/components/AppLayout.vue";
import { dashboard } from "@/models/dashboard";
import formaterService from "@/services/formaterService";

export default {
  name: 'PainelView',
  components: {
    AppLayout
  },
  setup(props) {
    const resumo = ref(null);
    const Dashboard = dashboard();
    const salesChartCanvas = ref(null);
    let salesChart = null;

    const lucroPositivo = computed(() => (resumo.value?.profit_month_total ?? 0) >= 0);

    // formaterService.date() usa `new Date('YYYY-MM-DD')`, que o JS interpreta
    // como meia-noite UTC — em fusos atrás do UTC (ex.: Brasil) isso exibiria
    // o dia anterior. Aqui a data já vem sem horário, então formatamos
    // localmente sem passar pelo construtor Date.
    function labelDoDia(isoDate) {
      const [ano, mes, dia] = isoDate.split('-');
      return `${dia}/${mes}`;
    }

    // formaterService.currency() retorna null para 0 (usado nas telas com
    // `?? 'R$ 0,00'`); no gráfico precisamos do valor real formatado mesmo
    // quando for zero.
    function moeda(valor) {
      return (valor ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    function renderSalesChart(salesByDay) {
      if (!salesChartCanvas.value) return;

      const labels = salesByDay.map(dia => labelDoDia(dia.date));
      const totals = salesByDay.map(dia => dia.total);

      if (salesChart) {
        salesChart.data.labels = labels;
        salesChart.data.datasets[0].data = totals;
        salesChart.update();
        return;
      }

      salesChart = new Chart(salesChartCanvas.value, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Vendas',
            data: totals,
            borderColor: '#0d6efd',
            backgroundColor: 'rgba(13, 110, 253, 0.15)',
            tension: 0.2,
            fill: true,
          }],
        },
        options: {
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (context) => moeda(context.parsed.y),
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: (value) => moeda(value),
              },
            },
          },
        },
      });
    }

    onMounted(async () => {
      resumo.value = await Dashboard.summary();
      if (resumo.value?.sales_by_day) {
        // O canvas só existe no DOM depois que o v-else troca de lugar com o
        // "Carregando...", o que só acontece no próximo tick após resumo
        // mudar — sem isso, o canvas ainda é null aqui.
        await nextTick();
        renderSalesChart(resumo.value.sales_by_day);
      }
    });

    onBeforeUnmount(() => {
      salesChart?.destroy();
    });

    return {resumo, formaterService, salesChartCanvas, lucroPositivo}
  },
}
</script>
<style scoped>
.section-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-strong);
  margin-bottom: 0.75rem;
}

.stat-card__icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  margin-bottom: 0.75rem;
}
</style>
